import React, { useEffect, useMemo, useState } from "react";

import "./BitcoinNavigatorDashboard.scss";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Drawer, IconButton, Tab, Tabs } from "@mui/material";
import { connect } from "react-redux";
import moment from "moment-timezone";

import { bitcoinNavigatorLoad } from "../../appRedux/ducks/bitcoinNavigator";
import { portfolioSymbolSetter } from "../../appRedux/ducks/portfolioTicker";
import { wealthGuidedRebalancesGet } from "../../appRedux/ducks/wealthGuidedRebalancesGet";
import BeautifulSkeleton from "../common/Skeletons/BeautifulSkeleton";
import WSGuidedRebalancing from "../WealthSeriesOverview/components/WSGuidedRebalancing/WSGuidedRebalancing";
import WSGRModal from "../WealthSeriesOverview/components/WSGuidedRebalancing/WSGRModal/WSGRModal";
import WSContactSupport from "../WealthSeriesOverview/components/WSContactSupport/WSContactSupport";
import WSTelegramCTA from "../WealthSeriesOverview/components/WSTelegramCTA/WSTelegramCTA";
import WealthSeriesPortfolioPerformanceGraph from "../WealthSeriesPortfolioPage/components/WealthSeriesPortfolioPerformanceGraph/WealthSeriesPortfolioPerformanceGraph";
import WealthSeriesPortfolioTickerInfo from "../WealthSeriesPortfolioPage/components/WealthSeriesPortfolioTickerInfo/WealthSeriesPortfolioTickerInfo";
import WealthSeriesPortfolioTickers from "../WealthSeriesPortfolioPage/components/WealthSeriesPortfolioTickers/WealthSeriesPortfolioTickers";
import BitcoinNavigatorWordmark from "./BitcoinNavigatorWordmark";
import BitcoinRebalancingHistory from "./BitcoinRebalancingHistory";
import NexusSignalSummary from "./NexusSignalSummary";
import {
  BN_DEFAULT_DESCRIPTION,
  BN_DEFAULT_TITLE,
  BN_DISCLAIMER,
  BN_TELEGRAM_LINK,
} from "./constants";

// The guided-rebalance APIs identify Bitcoin Navigator by this wealth-series
// tier key (the backend upgrades it to Navigator_Auto for entitled users).
const BN_GUIDED_REBALANCE_TIER = "navigator";

/**
 * Single-page Bitcoin Navigator dashboard: a clean overview up top (model
 * header, performance, Allocation Signal + Current Position) and a tabbed
 * section below (Rebalancing History, Current Holdings). A ticker's
 * "View Details" opens that ticker's detail in a right-side drawer.
 * Bitcoin Navigator ships one model, so there is no card-grid or drill-in.
 *
 * Live prices: the Current Holdings table manages its own dxFeed socket
 * subscription and the drawer's price header streams through the TradingView
 * datafeed of its chart — the same wiring the other wealth series pages use.
 */
const BitcoinNavigatorDashboard = ({
  portfolio,
  performanceSummary,
  loading,
  portfolioTicker,
  loadBitcoinNavigator,
  setTicker,
  fetchGuidedRebalances,
}) => {
  const [timeframe, setTimeframe] = useState("1y");
  const [tab, setTab] = useState(0);
  const [guidedRebalancingStep, setGuidedRebalancingStep] = useState(0);
  const [showGuidedRebalancingModal, setShowGuidedRebalancingModal] =
    useState(false);
  const [pendingTabValue, setPendingTabValue] = useState(null);

  // Guided rebalancing takes over the page while its tab is active: the top
  // panel and the tab-bar CTAs hide so the wizard gets the full viewport.
  const isGuidedRebalancingFullPage = tab === 2;

  useEffect(() => {
    loadBitcoinNavigator();
  }, [loadBitcoinNavigator]);

  // One-shot fetch of any in-progress guided rebalance — this is what lets
  // the module resume straight to its results step (same contract as
  // WealthSeriesOverview).
  useEffect(() => {
    fetchGuidedRebalances(BN_GUIDED_REBALANCE_TIER);
  }, [fetchGuidedRebalances]);

  // Never open with a stale ticker drawer.
  useEffect(() => {
    setTicker("");
    return () => {
      setTicker("");
    };
  }, [setTicker]);

  // index.css forces `body { overflow: auto !important }`, which overrides
  // MUI's built-in scroll lock — toggle the app's `scroll-freeze` escape hatch
  // so only the drawer's scrollbar is visible while it's open.
  useEffect(() => {
    if (!portfolioTicker) return;
    document.body.classList.add("scroll-freeze");
    return () => {
      document.body.classList.remove("scroll-freeze");
    };
  }, [portfolioTicker]);

  const closeTicker = () => setTicker("");

  // Single-model product: the wizard's portfolio picker gets one entry,
  // whichever portfolio variant the backend resolved for this user.
  const guidedRebalancingPortfolios = useMemo(() => {
    if (!portfolio?.portfolio_id) return [];
    return [{ id: portfolio.portfolio_id, name: BN_DEFAULT_TITLE }];
  }, [portfolio]);

  // Leaving mid-selection (step 1) asks for confirmation first — same guard
  // WealthSeriesOverview applies on its tab strip.
  const handleTabChange = (_, value) => {
    if (tab === 2 && guidedRebalancingStep === 1 && value !== 2) {
      setPendingTabValue(value);
      setShowGuidedRebalancingModal(true);
      return;
    }
    setTab(value);
  };

  const handleGuidedRebalancingModalExit = () => {
    setShowGuidedRebalancingModal(false);
    if (pendingTabValue !== null) {
      setTab(pendingTabValue);
      setPendingTabValue(null);
    }
  };

  const handleExitToRebalancingHistory = () => {
    setTab(0);
  };

  const lastUpdated = useMemo(() => {
    if (!portfolio?.last_updated) return null;
    return moment(portfolio.last_updated)
      .tz("America/New_York")
      .format("dddd, MMM D, YYYY h:mm A");
  }, [portfolio]);

  // Surface the selected ticker's most-recent adjustment so the drawer's
  // "Adjustment Reasoning" tab renders (derived, no effect needed).
  const selectedTickerInfo = useMemo(() => {
    const empty = {
      reason: undefined,
      isBuy: undefined,
      date: undefined,
      oldW: undefined,
      newW: undefined,
    };
    if (!portfolioTicker) return empty;
    const rebalances = [...(portfolio?.adjustments || [])].sort(
      (a, b) =>
        new Date(b.rebalance_date).getTime() -
        new Date(a.rebalance_date).getTime(),
    );
    const latest = rebalances.find(
      (rebalance) => rebalance.adjustments?.[portfolioTicker],
    );
    const adjustment = latest?.adjustments?.[portfolioTicker];
    if (!adjustment) return empty;
    return {
      // Per-ticker reasoning when the AI provides it, otherwise the
      // rebalance-level rationale so the drawer tab always has substance.
      reason: adjustment.ai_rebalance_reason || latest.reason || "",
      isBuy: (adjustment.diff ?? 0) >= 0,
      date: latest.rebalance_date,
      oldW: adjustment.old,
      newW: adjustment.new,
    };
  }, [portfolioTicker, portfolio]);

  if (!loading && !portfolio) {
    return (
      <div className="bitcoin-navigator-dashboard">
        <div className="bitcoin-navigator-dashboard__header">
          <BitcoinNavigatorWordmark size="lg" />
        </div>
        <div className="bitcoin-navigator-dashboard__unavailable">
          Bitcoin Navigator data is unavailable right now. Please try again
          shortly.
        </div>
      </div>
    );
  }

  return (
    <div className="bitcoin-navigator-dashboard">
      {/* Header — wordmark + last updated, outside the panels. Hidden while
          guided rebalancing has the page. */}
      {!isGuidedRebalancingFullPage && (
        <div className="bitcoin-navigator-dashboard__header">
          <BitcoinNavigatorWordmark size="lg" />
          {lastUpdated && (
            <div className="bitcoin-navigator-dashboard__last-updated">
              Last updated: {lastUpdated} (EST)
            </div>
          )}
        </div>
      )}

      {/* TOP — left: model header + performance, right: allocation signal.
          Hidden while guided rebalancing has the page. */}
      {!isGuidedRebalancingFullPage && (
        <div className="bitcoin-navigator-dashboard__top-card">
          <div className="bitcoin-navigator-dashboard__top-card-inner">
            <div className="bitcoin-navigator-dashboard__performance-panel">
              <h1 className="bitcoin-navigator-dashboard__title">
                {BN_DEFAULT_TITLE}
              </h1>
              <p className="bitcoin-navigator-dashboard__description">
                {BN_DEFAULT_DESCRIPTION}
              </p>
              <div className="bitcoin-navigator-dashboard__graph">
                <WealthSeriesPortfolioPerformanceGraph
                  tabValue={0}
                  timeframe={timeframe}
                  setTimeframe={setTimeframe}
                  portfolioData={portfolio}
                  simulatedCutoff={null}
                  enableBtcBenchmark
                />
              </div>
            </div>
            <div className="bitcoin-navigator-dashboard__signal-panel">
              {loading ? (
                <>
                  <BeautifulSkeleton width={160} height={28} />
                  <BeautifulSkeleton width="100%" height={180} />
                  <BeautifulSkeleton width="100%" height={90} />
                </>
              ) : (
                <NexusSignalSummary
                  portfolio={portfolio}
                  performanceSummary={performanceSummary}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* BOTTOM — tabbed section */}
      <div className="bitcoin-navigator-dashboard__bottom">
        <div className="bitcoin-navigator-dashboard__tabs-bar">
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            className="bitcoin-navigator-dashboard__tabs"
          >
            <Tab label="Rebalancing History" value={0} />
            <Tab label="Current Holdings" value={1} />
            <Tab label="Guided Rebalancing" value={2} />
          </Tabs>
          {!isGuidedRebalancingFullPage && (
            <div className="bitcoin-navigator-dashboard__tab-ctas">
              <WSTelegramCTA link={BN_TELEGRAM_LINK} />
              <WSContactSupport />
            </div>
          )}
        </div>

        {/* Mobile-only telegram entry point — the tab-bar CTAs are desktop */}
        {!isGuidedRebalancingFullPage && (
          <div className="bitcoin-navigator-dashboard__mobile-telegram">
            <WSTelegramCTA
              link={BN_TELEGRAM_LINK}
              className="ws-telegram-mobile"
            />
          </div>
        )}

        <div className="bitcoin-navigator-dashboard__panel">
          {tab === 0 && (
            <BitcoinRebalancingHistory
              portfolioData={portfolio}
              onViewTicker={(ticker) => setTicker(ticker)}
            />
          )}
          {tab === 1 && (
            <WealthSeriesPortfolioTickers
              setSelectedTickerInfo={() => {}}
              timeframe={timeframe}
              portfolioData={portfolio}
              loading={loading}
              isViewingPastDate={false}
              setIsViewingPastDate={() => {}}
            />
          )}
          {tab === 2 && (
            <div className="ws-guided-rebalancing-fullpage bitcoin-navigator-dashboard__guided-rebalancing">
              <WSGuidedRebalancing
                tier={BN_GUIDED_REBALANCE_TIER}
                tierDisplayName="Bitcoin Navigator"
                portfolios={guidedRebalancingPortfolios}
                onExitToPortfolioActivity={handleExitToRebalancingHistory}
                onStepChange={setGuidedRebalancingStep}
              />
            </div>
          )}
        </div>
      </div>

      {/* Confirms tab switches away from an in-progress guided rebalance */}
      <WSGRModal
        open={showGuidedRebalancingModal}
        onClose={() => setShowGuidedRebalancingModal(false)}
        onExit={handleGuidedRebalancingModalExit}
      />

      {/* Footer disclaimer */}
      <div className="bitcoin-navigator-dashboard__disclaimer">
        {BN_DISCLAIMER}
      </div>

      {/* Ticker detail — right-side slide-over drawer. z-index sits above the
          fixed sidebar (z-index 13000) so the overlay dims the nav too. */}
      <Drawer
        anchor="right"
        open={Boolean(portfolioTicker)}
        onClose={closeTicker}
        sx={{ zIndex: 13001 }}
        PaperProps={{
          className: "bitcoin-navigator-dashboard__drawer-paper",
          sx: { width: { xs: "100%", sm: 540 }, maxWidth: "100%" },
        }}
      >
        <div className="bitcoin-navigator-dashboard__drawer">
          <IconButton
            aria-label="Close ticker detail"
            onClick={closeTicker}
            size="small"
            className="bitcoin-navigator-dashboard__drawer-close"
          >
            <CloseRoundedIcon />
          </IconButton>
          <div className="bitcoin-navigator-dashboard__drawer-scroll bn-drawer-scroll">
            {portfolioTicker && (
              <WealthSeriesPortfolioTickerInfo
                ticker={portfolioTicker}
                selectedTickerInfo={selectedTickerInfo}
                navigator
                containerClassName=".bn-drawer-scroll"
              />
            )}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

const stateToProps = (state) => ({
  portfolio: state.bitcoinNavigator.portfolio,
  performanceSummary: state.bitcoinNavigator.performanceSummary,
  loading: state.bitcoinNavigator.loading,
  portfolioTicker: state.portfolioTicker.symbol,
});

const dispatchToProps = (dispatch) => ({
  loadBitcoinNavigator: () => dispatch(bitcoinNavigatorLoad()),
  setTicker: (symbol) => dispatch(portfolioSymbolSetter(symbol)),
  fetchGuidedRebalances: (tier) => dispatch(wealthGuidedRebalancesGet(tier)),
});

export default connect(
  stateToProps,
  dispatchToProps,
)(BitcoinNavigatorDashboard);
